/**
 * Mobile Revival - Shopify 2.0 Theme JavaScript
 * Vanilla JS, no dependencies. ES6+.
 */

(function () {
  'use strict';

  /* ==========================================================================
     Utility Helpers
     ========================================================================== */

  function debounce(fn, delay) {
    let timer = null;
    return function () {
      const context = this;
      const args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, delay);
    };
  }

  function formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    return '$' + dollars;
  }

  function escapeHtml(str) {
    if (typeof str !== 'string') return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function trapFocus(container) {
    const focusable = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    container.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  }

  /* Body scroll lock — counter pattern so multiple drawers/modals coexist */
  var _bodyLockCount = 0;
  function lockBodyScroll() {
    if (_bodyLockCount === 0) document.body.style.overflow = 'hidden';
    _bodyLockCount++;
  }
  function unlockBodyScroll() {
    _bodyLockCount = Math.max(0, _bodyLockCount - 1);
    if (_bodyLockCount === 0) document.body.style.overflow = '';
  }

  /* ==========================================================================
     1. Mobile Menu Toggle
     ========================================================================== */

  function initMobileMenu() {
    // Mobile menu open/close and accordion handling lives in the inline
    // <script> at the bottom of header.liquid (section-scoped, uses data
    // attributes, manages aria state).  Adding duplicate handlers here
    // caused a race condition where the toggle opened then immediately
    // closed the menu.  This stub is kept so safeInit('MobileMenu', …)
    // does not throw.

    var menu = document.querySelector('.mobile-menu');
    if (!menu) return;

    // Escape key fallback (inline script does not add one)
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && menu.classList.contains('is-active')) {
        menu.classList.remove('is-active');
        var overlay = document.querySelector('.mobile-menu__overlay');
        if (overlay) overlay.classList.remove('is-active');
        document.body.style.overflow = '';
      }
    });
  }

  /* ==========================================================================
     2. Mega Menu (Desktop)
     ========================================================================== */

  function initMegaMenu() {
    var navItems = document.querySelectorAll('.header__nav-item[data-megamenu]');
    var panels = document.querySelectorAll('.megamenu[data-megamenu]');
    if (!navItems.length || !panels.length) return;

    function hideAll() {
      panels.forEach(function (p) { p.hidden = true; });
      navItems.forEach(function (n) { n.classList.remove('is-active'); });
    }

    function showFor(brand) {
      var target = document.querySelector('.megamenu[data-megamenu="' + brand + '"]');
      if (!target) return;
      hideAll();
      target.hidden = false;
      var item = document.querySelector('.header__nav-item[data-megamenu="' + brand + '"]');
      if (item) item.classList.add('is-active');
    }

    var hoverTimer = null;

    navItems.forEach(function (item) {
      var brand = item.getAttribute('data-megamenu');
      var link = item.querySelector('a');

      item.addEventListener('mouseenter', function () {
        clearTimeout(hoverTimer);
        showFor(brand);
      });

      item.addEventListener('mouseleave', function () {
        hoverTimer = setTimeout(hideAll, 150);
      });

      // Keyboard: open on focus + Enter/Space
      if (link) {
        link.addEventListener('focus', function () { showFor(brand); });
        link.addEventListener('keydown', function (e) {
          if (e.key === 'Escape') { hideAll(); link.blur(); }
        });
      }
    });

    // Keep panel open when hovering panel itself
    panels.forEach(function (panel) {
      panel.addEventListener('mouseenter', function () { clearTimeout(hoverTimer); });
      panel.addEventListener('mouseleave', function () { hoverTimer = setTimeout(hideAll, 150); });
    });

    // Close on outside click
    document.addEventListener('click', function (e) {
      if (!e.target.closest('.header')) hideAll();
    });

    // Close on Escape anywhere
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') hideAll();
    });
  }

  /* ==========================================================================
     Header: Ship Timer
     ========================================================================== */

  function initShipTimer() {
    var pill = document.querySelector('.header__ship-pill');
    if (!pill) return;

    var timeEl = pill.querySelector('.header__ship-time');
    var labelEl = pill.querySelector('.header__ship-label');
    var cutoffStr = pill.getAttribute('data-ship-cutoff') || '09:00';
    var tzStr = pill.getAttribute('data-ship-tz') || 'CST';

    // Parse cutoff "HH:MM"
    var parts = cutoffStr.split(':');
    var cutoffH = parseInt(parts[0], 10) || 9;
    var cutoffM = parseInt(parts[1], 10) || 0;

    // Map common US shorthand to IANA timezones
    var tzMap = { CST: 'America/Chicago', CDT: 'America/Chicago', EST: 'America/New_York', EDT: 'America/New_York', PST: 'America/Los_Angeles', PDT: 'America/Los_Angeles', MST: 'America/Denver', MDT: 'America/Denver' };
    var tz = tzMap[tzStr.toUpperCase()] || 'America/Chicago';

    function nowInTz() {
      var fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hour12: false,
        weekday: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
      var parts = fmt.formatToParts(new Date());
      var weekday = parts.find(function (p) { return p.type === 'weekday'; }).value;
      var h = parseInt(parts.find(function (p) { return p.type === 'hour'; }).value, 10) || 0;
      var m = parseInt(parts.find(function (p) { return p.type === 'minute'; }).value, 10) || 0;
      var s = parseInt(parts.find(function (p) { return p.type === 'second'; }).value, 10) || 0;
      if (h === 24) h = 0;
      return { weekday: weekday, h: h, m: m, s: s };
    }

    function updatePill() {
      var t = nowInTz();
      var isWeekend = t.weekday === 'Sat' || t.weekday === 'Sun';

      if (isWeekend) {
        pill.setAttribute('data-ship-state', 'weekend');
        if (labelEl) labelEl.textContent = 'Ships';
        if (timeEl) timeEl.textContent = 'Mon, ' + cutoffStr + ' ' + tzStr;
        return;
      }

      var nowSec = t.h * 3600 + t.m * 60 + t.s;
      var cutoffSec = cutoffH * 3600 + cutoffM * 60;
      var deltaSec = cutoffSec - nowSec;

      if (deltaSec <= 0) {
        pill.setAttribute('data-ship-state', 'closed');
        if (labelEl) labelEl.textContent = 'Ships';
        if (timeEl) timeEl.textContent = 'next business day';
        return;
      }

      pill.setAttribute('data-ship-state', 'open');
      if (labelEl) labelEl.textContent = 'Ships in';
      var hh = Math.floor(deltaSec / 3600);
      var mm = Math.floor((deltaSec % 3600) / 60);
      var ss = deltaSec % 60;
      var pad = function (n) { return n < 10 ? '0' + n : '' + n; };
      if (timeEl) timeEl.textContent = pad(hh) + ':' + pad(mm) + ':' + pad(ss);
    }

    updatePill();
    setInterval(updatePill, 1000);
  }

  /* ==========================================================================
     Header: Search Shortcut (Cmd+K / Ctrl+K)
     ========================================================================== */

  function initSearchShortcut() {
    var input = document.querySelector('.header__search-input');
    if (!input) return;

    document.addEventListener('keydown', function (e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        input.focus();
        input.select();
      }
      if (e.key === 'Escape' && document.activeElement === input) {
        input.value = '';
        input.blur();
      }
    });
  }

  /* ==========================================================================
     Header: Search Placeholder Rotation
     ========================================================================== */

  function initSearchPlaceholderRotate() {
    var input = document.querySelector('.header__search-input');
    if (!input) return;

    var basePlaceholder = input.placeholder;
    var rotations = [
      basePlaceholder,
      'Search by model — A2179, Switch 2…',
      'Search by device — MacBook Pro 16, Switch 2, ROG Ally…'
    ];
    var idx = 0;

    setInterval(function () {
      if (document.activeElement === input || input.value.length > 0) return;
      idx = (idx + 1) % rotations.length;
      input.placeholder = rotations[idx];
    }, 4000);
  }

  /* ==========================================================================
     Header: Scroll Collapse
     ========================================================================== */

  function initHeaderScrollCollapse() {
    var header = document.querySelector('.header');
    if (!header) return;

    // Use scroll position instead of IntersectionObserver — IO got stuck
    // after the announcement bar dismiss animation rearranged the layout
    // (utility row never re-intersected after the layout shift).
    // Pixel threshold is more predictable.
    var threshold = 80;
    var ticking = false;

    function update() {
      ticking = false;
      if (window.scrollY > threshold) header.classList.add('is-scrolled');
      else header.classList.remove('is-scrolled');
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ==========================================================================
     Header: Mobile Drawer (v2)
     ========================================================================== */

  function initMobileHeaderDrawer() {
    var toggle = document.querySelector('[data-mobile-toggle]');
    var drawer = document.querySelector('[data-mobile-drawer]');
    var overlay = document.querySelector('[data-mobile-overlay]');
    var close = drawer ? drawer.querySelector('.header__mobile-close') : null;
    if (!toggle || !drawer || !overlay) return;

    function open() {
      drawer.hidden = false;
      overlay.hidden = false;
      requestAnimationFrame(function () {
        drawer.classList.add('is-open');
        overlay.classList.add('is-visible');
        if (typeof lockBodyScroll === 'function') lockBodyScroll();
      });
      toggle.setAttribute('aria-expanded', 'true');
    }

    function shut() {
      drawer.classList.remove('is-open');
      overlay.classList.remove('is-visible');
      toggle.setAttribute('aria-expanded', 'false');
      setTimeout(function () {
        drawer.hidden = true;
        overlay.hidden = true;
        if (typeof unlockBodyScroll === 'function') unlockBodyScroll();
      }, 250);
    }

    toggle.addEventListener('click', open);
    if (close) close.addEventListener('click', shut);
    overlay.addEventListener('click', shut);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) shut();
    });
  }

  /* ==========================================================================
     3. Announcement Bar Dismiss
     ========================================================================== */

  function initAnnouncementBar() {
    var bar = document.querySelector('.announcement-bar');
    var dismissBtn = document.querySelector('.announcement-bar__dismiss');

    if (!bar) return;

    if (sessionStorage.getItem('mr_announcement_dismissed') === 'true') {
      bar.style.display = 'none';
      return;
    }

    if (dismissBtn) {
      dismissBtn.addEventListener('click', function () {
        bar.style.transition = 'max-height 0.3s ease, opacity 0.3s ease, padding 0.3s ease';
        bar.style.maxHeight = bar.scrollHeight + 'px';
        requestAnimationFrame(function () {
          bar.style.maxHeight = '0';
          bar.style.opacity = '0';
          bar.style.paddingTop = '0';
          bar.style.paddingBottom = '0';
          bar.style.overflow = 'hidden';
        });
        sessionStorage.setItem('mr_announcement_dismissed', 'true');
        setTimeout(function () {
          bar.style.display = 'none';
        }, 320);
      });
    }
  }

  /* ==========================================================================
     4. Predictive Search
     ========================================================================== */

  function initPredictiveSearch() {
    var input = document.querySelector('.header__search-input');
    var resultsContainer = document.querySelector('.predictive-search');
    var searchForm = document.querySelector('.header__search-form');
    var abortController = null;

    if (!input || !resultsContainer) return;

    function showResults() {
      resultsContainer.classList.add('is-active');
    }

    function hideResults() {
      resultsContainer.classList.remove('is-active');
    }

    function showLoading() {
      resultsContainer.innerHTML = '<div class="predictive-search__loading">Searching...</div>';
      showResults();
    }

    function renderResults(products) {
      if (!products || products.length === 0) {
        resultsContainer.innerHTML = '<div class="predictive-search__empty">No results found</div>';
        showResults();
        return;
      }

      var html = '<ul class="predictive-search__list">';
      products.forEach(function (product) {
        var image = product.image || product.featured_image || '';
        var imageUrl = '';
        if (typeof image === 'string') {
          imageUrl = image;
        } else if (image && image.url) {
          imageUrl = image.url;
        }
        var price = product.price ? formatMoney(Math.round(parseFloat(product.price) * 100)) : '';
        var title = escapeHtml(product.title || '');
        var url = escapeHtml(product.url || '/products/' + (product.handle || ''));
        var safeImageUrl = escapeHtml(imageUrl);

        html += '<li class="predictive-search__item">';
        html += '<a href="' + url + '" class="predictive-search__link">';
        if (imageUrl) {
          html += '<img class="predictive-search__image" src="' + safeImageUrl + '" alt="' + title + '" width="60" height="60" loading="lazy">';
        }
        html += '<div class="predictive-search__info">';
        html += '<span class="predictive-search__title">' + title + '</span>';
        if (price) {
          html += '<span class="predictive-search__price">' + price + '</span>';
        }
        html += '</div>';
        html += '</a>';
        html += '</li>';
      });
      html += '</ul>';

      resultsContainer.innerHTML = html;
      showResults();
    }

    function fetchResults(query) {
      if (abortController) {
        abortController.abort();
      }
      abortController = new AbortController();

      var encodedQuery = encodeURIComponent(query);
      var url = '/search/suggest.json?q=' + encodedQuery + '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=hide';

      showLoading();

      fetch(url, { signal: abortController.signal })
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          var products = [];
          if (data.resources && data.resources.results && data.resources.results.products) {
            products = data.resources.results.products;
          }
          renderResults(products);
        })
        .catch(function (err) {
          if (err.name !== 'AbortError') {
            resultsContainer.innerHTML = '<div class="predictive-search__error">Something went wrong</div>';
            showResults();
          }
        });
    }

    var debouncedFetch = debounce(function () {
      var query = input.value.trim();
      if (query.length < 2) {
        hideResults();
        resultsContainer.innerHTML = '';
        return;
      }
      fetchResults(query);
    }, 300);

    input.addEventListener('input', debouncedFetch);

    input.addEventListener('focus', function () {
      if (input.value.trim().length >= 2 && resultsContainer.innerHTML.trim() !== '') {
        showResults();
      }
    });

    // Close on click outside
    document.addEventListener('click', function (e) {
      if (searchForm && !searchForm.contains(e.target) && !resultsContainer.contains(e.target)) {
        hideResults();
      }
    });

    // Close on Escape
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        hideResults();
        input.blur();
      }
    });
  }

  /* ==========================================================================
     5. AJAX Cart (Add to Cart)
     ========================================================================== */

  function isCartAddForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    var action = form.getAttribute('action') || '';
    return action === '/cart/add' || action.indexOf('/cart/add') !== -1;
  }

  function initAjaxCart() {
    // Intercept all add-to-cart form submissions in capture phase
    // (fires before Shopify's own form handlers)
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!isCartAddForm(form)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      submitAddToCart(form);
    }, true);

    // Also intercept clicks on submit buttons inside cart forms in capture phase
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('button[type="submit"], input[type="submit"]');
      if (!btn) return;
      var form = btn.closest('form');
      if (!isCartAddForm(form)) return;

      // Quick-add button path: use data-variant-id if available for a faster JSON request
      var quickAddBtn = btn.closest('.product-card__quick-add');
      if (quickAddBtn) {
        e.preventDefault();
        e.stopImmediatePropagation();

        var variantId = quickAddBtn.getAttribute('data-variant-id');
        // Fallback: read from the form's hidden input
        if (!variantId) {
          var hiddenInput = form.querySelector('input[name="id"]');
          if (hiddenInput) variantId = hiddenInput.value;
        }
        if (!variantId) return;

        quickAddBtn.classList.add('is-loading');
        quickAddBtn.disabled = true;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
        })
          .then(function (response) {
            if (!response.ok) {
              return response.json().then(function (err) {
                throw new Error(err.description || 'Could not add item to cart');
              });
            }
            return response.json();
          })
          .then(function () {
            updateCartCount();
            quickAddBtn.classList.remove('is-loading');
            quickAddBtn.disabled = false;
            if (window.MR && typeof window.MR.openCart === 'function') {
              window.MR.openCart();
            } else {
              window.location.href = '/cart';
            }
          })
          .catch(function (err) {
            showCartError(err.message || 'Item is out of stock');
            quickAddBtn.classList.remove('is-loading');
            quickAddBtn.disabled = false;
          });
        return;
      }

      // Regular add-to-cart button
      e.preventDefault();
      e.stopImmediatePropagation();
      submitAddToCart(form);
    }, true);
  }

  function submitAddToCart(form) {
    var submitBtn = form.querySelector('[type="submit"]');
    var originalText = '';
    if (submitBtn) {
      originalText = submitBtn.textContent;
      submitBtn.textContent = 'Adding...';
      submitBtn.disabled = true;
    }

    var variantInput = form.querySelector('input[name="id"]');
    var quantityInput = form.querySelector('input[name="quantity"]');
    var variantId = variantInput ? variantInput.value : '';
    var quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

    if (!variantId) {
      showCartError('Please select a product variant');
      if (submitBtn) {
        submitBtn.textContent = originalText;
        submitBtn.disabled = false;
      }
      return;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: quantity }] })
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (err) {
            throw new Error(err.description || 'Could not add item to cart');
          });
        }
        return response.json();
      })
      .then(function () {
        if (submitBtn) {
          submitBtn.textContent = 'Added to cart';
          submitBtn.disabled = false;
        }
        if (window.MR && typeof window.MR.openCart === 'function') {
          window.MR.openCart();
          if (submitBtn) {
            setTimeout(function () { submitBtn.textContent = originalText; }, 1200);
          }
        } else {
          window.location.href = '/cart';
        }
      })
      .catch(function (err) {
        showCartError(err.message || 'Item is out of stock');
        if (submitBtn) {
          submitBtn.textContent = originalText;
          submitBtn.disabled = false;
        }
      });
  }

  function showCartError(message) {
    var existing = document.querySelector('.cart-error-toast');
    if (existing) existing.remove();

    var toast = document.createElement('div');
    toast.className = 'cart-error-toast';
    toast.textContent = message;
    toast.setAttribute('role', 'alert');
    document.body.appendChild(toast);

    requestAnimationFrame(function () {
      toast.classList.add('is-visible');
    });

    setTimeout(function () {
      toast.classList.remove('is-visible');
      setTimeout(function () {
        toast.remove();
      }, 300);
    }, 3500);
  }

  function updateCartCount() {
    fetch('/cart.js')
      .then(function (r) {
        if (!r.ok) throw new Error('Cart fetch failed: ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        var badges = document.querySelectorAll('[data-cart-count]');
        badges.forEach(function (badge) {
          badge.textContent = cart.item_count > 0 ? cart.item_count : '';
          badge.setAttribute('data-count', cart.item_count);
        });
        updateShippingBar(cart.total_price);
      })
      .catch(function (err) {
        console.error('updateCartCount error:', err);
      });
  }

  /* ==========================================================================
     6. Cart Drawer
     ========================================================================== */

  function initCartDrawer() {
    var drawer = document.querySelector('.cart-drawer');
    var overlay = document.querySelector('.cart-drawer__overlay');
    var closeBtn = document.querySelector('.cart-drawer__close');

    if (!drawer) return;

    // Cart link clicks navigate to /cart page naturally (no interception).
    // The drawer is only opened programmatically after AJAX add-to-cart.

    if (closeBtn) {
      closeBtn.addEventListener('click', closeCartDrawer);
    }

    if (overlay) {
      overlay.addEventListener('click', closeCartDrawer);
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('cart-drawer--open')) {
        closeCartDrawer();
      }
    });
  }

  function openCartDrawer() {
    var drawer = document.querySelector('.cart-drawer');
    var overlay = document.querySelector('.cart-drawer__overlay');
    if (!drawer) {
      window.location.href = '/cart';
      return;
    }

    // Show loading spinner immediately so drawer isn't a blank white screen
    var container = drawer.querySelector('.cart-drawer__items');
    if (container) {
      container.innerHTML = '<div class="cart-drawer__loading"><div class="cart-drawer__spinner"></div><p>Loading cart&hellip;</p></div>';
    }

    drawer.classList.add('cart-drawer--open');
    drawer.setAttribute('aria-hidden', 'false');
    if (overlay) {
      overlay.classList.add('cart-drawer__overlay--visible');
      overlay.setAttribute('aria-hidden', 'false');
    }
    lockBodyScroll();

    fetchAndRenderCart();
    // I3 fix: trapFocus only once per page lifecycle (was accumulating listener on every open)
    if (!drawer.dataset.focusTrapped) {
      trapFocus(drawer);
      drawer.dataset.focusTrapped = '1';
    }
  }

  // Expose cart functions globally for inline handlers
  window.MR = window.MR || {};
  window.MR.openCart = openCartDrawer;
  window.MR.closeCart = closeCartDrawer;

  function closeCartDrawer() {
    var drawer = document.querySelector('.cart-drawer');
    var overlay = document.querySelector('.cart-drawer__overlay');
    if (!drawer) return;

    drawer.classList.remove('cart-drawer--open');
    drawer.setAttribute('aria-hidden', 'true');
    if (overlay) {
      overlay.classList.remove('cart-drawer__overlay--visible');
      overlay.setAttribute('aria-hidden', 'true');
    }
    unlockBodyScroll();
  }

  function fetchAndRenderCart() {
    var container = document.querySelector('.cart-drawer__items');
    var subtotalEl = document.querySelector('.cart-drawer__subtotal-price');
    var checkoutBtn = document.querySelector('.cart-drawer__checkout');
    var countEl = document.querySelector('.cart-drawer__count');

    fetch('/cart.js')
      .then(function (r) {
        if (!r.ok) throw new Error('Cart fetch failed: ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        if (!container) return;

        if (countEl) countEl.textContent = cart.item_count;

        if (cart.items.length === 0) {
          container.innerHTML = '<div class="cart-drawer__empty"><p>Your cart is empty</p></div>';
          if (checkoutBtn) {
            checkoutBtn.setAttribute('aria-disabled', 'true');
            checkoutBtn.setAttribute('tabindex', '-1');
            checkoutBtn.style.pointerEvents = 'none';
            checkoutBtn.style.opacity = '0.6';
          }
          if (subtotalEl) subtotalEl.textContent = formatMoney(0);
          updateShippingBar(0);
          return;
        }

        if (checkoutBtn) {
          checkoutBtn.removeAttribute('aria-disabled');
          checkoutBtn.removeAttribute('tabindex');
          checkoutBtn.style.pointerEvents = '';
          checkoutBtn.style.opacity = '';
        }

        var html = '';
        cart.items.forEach(function (item, index) {
          var imageUrl = escapeHtml(item.image || (item.featured_image ? item.featured_image.url : '') || '');
          var variantTitle = escapeHtml(item.variant_title || '');
          var productTitle = escapeHtml(item.product_title || '');
          var itemUrl = escapeHtml(item.url || '');
          var itemKey = escapeHtml(item.key || '');
          var itemTitle = escapeHtml(item.title || '');
          var lineIndex = index + 1;
          var safeQty = parseInt(item.quantity, 10) || 1;
          html += '<div class="cart-drawer-item" data-key="' + itemKey + '" data-line="' + lineIndex + '">';
          html += '  <a href="' + itemUrl + '" class="cart-drawer-item__image">';
          if (item.image || (item.featured_image && item.featured_image.url)) {
            html += '    <img src="' + imageUrl + '" alt="' + itemTitle + '" width="60" height="60" loading="lazy">';
          }
          html += '  </a>';
          html += '  <div class="cart-drawer-item__info">';
          html += '    <a href="' + itemUrl + '" class="cart-drawer-item__title">' + productTitle + '</a>';
          if (variantTitle) {
            html += '    <p class="cart-drawer-item__variant">' + variantTitle + '</p>';
          }
          html += '    <div class="cart-drawer-item__bottom">';
          html += '      <div class="quantity quantity--sm">';
          html += '        <button class="quantity__btn quantity__btn--minus" type="button" data-action="decrease" data-key="' + itemKey + '" aria-label="Decrease quantity">-</button>';
          html += '        <input type="number" value="' + safeQty + '" min="1" max="999" class="quantity__input" data-key="' + itemKey + '" aria-label="Quantity">';
          html += '        <button class="quantity__btn quantity__btn--plus" type="button" data-action="increase" data-key="' + itemKey + '" aria-label="Increase quantity">+</button>';
          html += '      </div>';
          html += '      <span class="cart-drawer-item__price">' + formatMoney(item.final_line_price) + '</span>';
          html += '    </div>';
          html += '  </div>';
          html += '  <button class="cart-drawer-item__remove" data-key="' + itemKey + '" aria-label="Remove ' + productTitle + '">';
          html += '    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
          html += '  </button>';
          html += '</div>';
        });

        container.innerHTML = html;

        if (subtotalEl) {
          subtotalEl.textContent = formatMoney(cart.total_price);
        }

        updateShippingBar(cart.total_price);

        // Update cart count badges
        var badges = document.querySelectorAll('[data-cart-count]');
        badges.forEach(function (badge) {
          badge.textContent = cart.item_count > 0 ? cart.item_count : '';
          badge.setAttribute('data-count', cart.item_count);
        });
      })
      .catch(function (err) {
        console.error('fetchAndRenderCart error:', err);
        if (container) {
          container.innerHTML = '<p style="padding:1rem;color:#666;">Unable to load cart. <a href="/cart">View cart page</a></p>';
        }
      });
  }

  function initCartDrawerEvents() {
    document.addEventListener('click', function (e) {
      var qtyBtn = e.target.closest('.quantity__btn');
      if (qtyBtn && qtyBtn.closest('.cart-drawer')) {
        var key = qtyBtn.dataset.key || qtyBtn.dataset.line;
        var action = qtyBtn.classList.contains('quantity__btn--plus') ? 'increase' : 'decrease';
        var inputEl = qtyBtn.parentElement.querySelector('.quantity__input');
        var currentQty = parseInt(inputEl.value, 10) || 1;
        var newQty = action === 'increase' ? currentQty + 1 : currentQty - 1;

        if (newQty < 1) newQty = 0;
        if (newQty > 999) newQty = 999;
        changeCartItem(key, newQty);
        return;
      }

      var removeBtn = e.target.closest('.cart-drawer-item__remove, .cart-line--drawer .cart-line__remove, .cart-line__remove');
      if (removeBtn && removeBtn.closest('.cart-drawer')) {
        var removeKey = removeBtn.dataset.key || removeBtn.dataset.line;
        changeCartItem(removeKey, 0);
      }
    });
  }

  function changeCartItem(key, quantity) {
    // Numeric key => Liquid-rendered v2 markup (data-line, 1-based index).
    // String key => JS-rendered fallback markup (data-key, item.key hash).
    var payload = { quantity: quantity };
    var lineIdx = parseInt(key, 10);
    if (typeof key === 'string' && /^\d+$/.test(key) && lineIdx > 0) {
      payload.line = lineIdx;
    } else {
      payload.id = key;
    }
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Cart change failed: ' + r.status);
        return r.json();
      })
      .then(function () {
        fetchAndRenderCart();
        updateCartCount();
      })
      .catch(function (err) {
        console.error('changeCartItem error:', err);
        showCartError('Failed to update cart. Please try again.');
      });
  }

  /* ==========================================================================
     7. Product Variant Selector
     ========================================================================== */

  function initVariantSelector() {
    var productData = document.querySelector('[data-product-json]');
    if (!productData) return;

    var product;
    try {
      product = JSON.parse(productData.textContent);
    } catch (e) {
      return;
    }

    var variantSelectors = document.querySelectorAll('[data-option-radio], [data-option-select]');
    if (variantSelectors.length === 0) return;

    function getSelectedOptions() {
      var options = [];
      var optionGroups = document.querySelectorAll('.product__option');
      optionGroups.forEach(function (group) {
        var selected = group.querySelector('input[type="radio"]:checked');
        if (selected) {
          options.push(selected.value);
        } else {
          var selectEl = group.querySelector('select');
          if (selectEl) {
            options.push(selectEl.value);
          }
        }
      });
      return options;
    }

    function findVariant(selectedOptions) {
      return product.variants.find(function (variant) {
        return variant.options.every(function (opt, index) {
          return opt === selectedOptions[index];
        });
      });
    }

    function updateProductPage(variant) {
      var priceEl = document.querySelector('.product__price-wrapper .price--sale, .product__price-wrapper .price__regular');
      var comparePriceEl = document.querySelector('.product__price-wrapper .price--compare');
      var skuEl = document.querySelector('[data-sku-value]');
      var availabilityEl = document.querySelector('[data-product-availability]');
      var variantInput = document.querySelector('[data-variant-id]');
      var addToCartBtn = document.querySelector('[data-add-to-cart]');
      var mainImage = document.querySelector('#product-featured-image');

      if (!variant) {
        if (addToCartBtn) {
          addToCartBtn.disabled = true;
          addToCartBtn.textContent = 'Unavailable';
        }
        if (availabilityEl) {
          availabilityEl.textContent = 'Unavailable';
          availabilityEl.classList.add('is-unavailable');
        }
        return;
      }

      if (priceEl) {
        priceEl.textContent = formatMoney(variant.price);
      }

      if (comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePriceEl.textContent = formatMoney(variant.compare_at_price);
          comparePriceEl.style.display = '';
        } else {
          comparePriceEl.textContent = '';
          comparePriceEl.style.display = 'none';
        }
      }

      if (skuEl) {
        skuEl.textContent = variant.sku || '';
      }

      if (variantInput) {
        variantInput.value = variant.id;
      }

      if (variant.available) {
        if (addToCartBtn) {
          addToCartBtn.disabled = false;
          addToCartBtn.textContent = 'Add to Cart';
        }
        if (availabilityEl) {
          availabilityEl.textContent = 'In stock';
          availabilityEl.classList.remove('is-unavailable');
          availabilityEl.classList.add('is-available');
        }
      } else {
        if (addToCartBtn) {
          addToCartBtn.disabled = true;
          addToCartBtn.textContent = 'Sold Out';
        }
        if (availabilityEl) {
          availabilityEl.textContent = 'Sold out';
          availabilityEl.classList.add('is-unavailable');
          availabilityEl.classList.remove('is-available');
        }
      }

      // Update product image if variant has featured image
      if (variant.featured_image && mainImage) {
        mainImage.src = variant.featured_image.src;
        mainImage.alt = variant.featured_image.alt || variant.title;
      }

      // Update URL without reload
      if (variant.id) {
        var newUrl = window.location.pathname + '?variant=' + variant.id;
        window.history.replaceState({ variant: variant.id }, '', newUrl);
      }

      // Dispatch variant:change event for other components (back-in-stock, etc.)
      document.dispatchEvent(new CustomEvent('variant:change', {
        detail: {
          variant: variant,
          productTitle: product.title,
          productHandle: product.handle
        }
      }));
    }

    variantSelectors.forEach(function (selector) {
      var eventType = selector.tagName === 'SELECT' ? 'change' : 'change';
      selector.addEventListener(eventType, function () {
        var selectedOptions = getSelectedOptions();
        var variant = findVariant(selectedOptions);
        updateProductPage(variant);
      });
    });
  }

  /* ==========================================================================
     8. Product Image Gallery
     ========================================================================== */

  function initProductGallery() {
    var mainImage = document.querySelector('#product-featured-image');
    var thumbnails = document.querySelectorAll('[data-thumbnail]');
    var galleryContainer = document.querySelector('.product__gallery');

    if (!mainImage) return;

    // Thumbnail click
    thumbnails.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var newSrc = thumb.getAttribute('data-image-src');
        var newSrcset = thumb.getAttribute('data-image-srcset');
        var newAlt = thumb.getAttribute('data-image-alt') || '';
        var newWidth = thumb.getAttribute('data-image-width');
        var newHeight = thumb.getAttribute('data-image-height');

        if (newSrc) mainImage.src = newSrc;
        if (newSrcset) mainImage.srcset = newSrcset;
        if (newAlt) mainImage.alt = newAlt;
        if (newWidth) mainImage.width = newWidth;
        if (newHeight) mainImage.height = newHeight;

        // Active thumbnail highlight
        thumbnails.forEach(function (t) {
          t.classList.remove('product__thumbnail--active');
        });
        thumb.classList.add('product__thumbnail--active');
      });
    });

    // Image zoom on hover (desktop)
    // Skip if v2 zoom IIFE is in charge (data-zoom attribute marks the v2 PDP).
    if (galleryContainer && window.matchMedia('(pointer: fine)').matches) {
      var mainImageWrap = mainImage.closest('.product__gallery-main');
      if (mainImageWrap && !mainImageWrap.hasAttribute('data-zoom')) {
        mainImageWrap.addEventListener('mouseenter', function () {
          mainImage.style.transition = 'transform 0.1s ease';
          mainImage.style.transform = 'scale(1.5)';
        });

        mainImageWrap.addEventListener('mousemove', function (e) {
          var rect = mainImageWrap.getBoundingClientRect();
          var x = ((e.clientX - rect.left) / rect.width) * 100;
          var y = ((e.clientY - rect.top) / rect.height) * 100;
          mainImage.style.transformOrigin = x + '% ' + y + '%';
        });

        mainImageWrap.addEventListener('mouseleave', function () {
          mainImage.style.transform = 'scale(1)';
          mainImage.style.transformOrigin = 'center center';
        });
      }
    }

    // Swipe support on mobile (touch events)
    var touchStartX = 0;
    var touchEndX = 0;
    var currentIndex = 0;

    if (galleryContainer) {
      galleryContainer.addEventListener('touchstart', function (e) {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      galleryContainer.addEventListener('touchend', function (e) {
        touchEndX = e.changedTouches[0].screenX;
        handleSwipe();
      }, { passive: true });
    }

    function handleSwipe() {
      var diff = touchStartX - touchEndX;
      var threshold = 50;

      if (thumbnails.length < 2) return;

      // Find current active index
      thumbnails.forEach(function (t, i) {
        if (t.classList.contains('product__thumbnail--active')) {
          currentIndex = i;
        }
      });

      if (diff > threshold) {
        // Swipe left -> next image
        currentIndex = (currentIndex + 1) % thumbnails.length;
      } else if (diff < -threshold) {
        // Swipe right -> prev image
        currentIndex = (currentIndex - 1 + thumbnails.length) % thumbnails.length;
      } else {
        return;
      }

      thumbnails[currentIndex].click();
    }
  }

  /* ==========================================================================
     9. Product Tabs
     ========================================================================== */

  function initProductTabs() {
    var tabContainers = document.querySelectorAll('.product__tabs');

    tabContainers.forEach(function (container) {
      var tabBtns = container.querySelectorAll('[data-tab-btn]');
      var tabPanels = container.querySelectorAll('[data-tab-panel]');

      if (tabBtns.length === 0 || tabPanels.length === 0) return;

      tabBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var target = btn.getAttribute('data-tab-btn');

          // Deactivate all
          tabBtns.forEach(function (b) {
            b.classList.remove('product__tab--active');
            b.setAttribute('aria-selected', 'false');
          });
          tabPanels.forEach(function (p) {
            p.classList.remove('product__tab-panel--active');
            p.hidden = true;
          });

          // Activate clicked
          btn.classList.add('product__tab--active');
          btn.setAttribute('aria-selected', 'true');
          var targetPanel = container.querySelector('[data-tab-panel="' + target + '"]');
          if (targetPanel) {
            targetPanel.classList.add('product__tab-panel--active');
            targetPanel.hidden = false;
          }
        });
      });
    });
  }

  /* ==========================================================================
     10. Sticky Add-to-Cart Bar
     ========================================================================== */

  function initStickyAddBar() {
    var stickyBar = document.querySelector('[data-sticky-add-bar]');
    var mainAddBtn = document.querySelector('[data-add-to-cart]');

    if (!stickyBar || !mainAddBtn) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            stickyBar.classList.remove('sticky-add-bar--visible');
            // M1 fix: also flip aria-hidden so screen readers don't see the bar's ATC button when it's offscreen
            stickyBar.setAttribute('aria-hidden', 'true');
          } else {
            stickyBar.classList.add('sticky-add-bar--visible');
            stickyBar.setAttribute('aria-hidden', 'false');
          }
        });
      },
      {
        root: null,
        threshold: 0,
        rootMargin: '0px'
      }
    );

    observer.observe(mainAddBtn);

    // Sticky bar "Add to Cart" triggers the main form
    var stickyAddBtn = stickyBar.querySelector('[data-sticky-add-btn]');
    if (stickyAddBtn) {
      stickyAddBtn.addEventListener('click', function (e) {
        e.preventDefault();
        var mainForm = mainAddBtn.closest('form[action="/cart/add"]') || mainAddBtn.closest('form[action*="/cart/add"]');
        if (mainForm) {
          submitAddToCart(mainForm);
        }
      });
    }
  }

  /* ==========================================================================
     12. Newsletter Popup
     ========================================================================== */

  function initNewsletterPopup() {
    var overlay = document.querySelector('.newsletter-popup-overlay');
    if (!overlay) return;

    // Don't fire on cart, checkout, or sell-your-device pages
    var path = window.location.pathname;
    if (path.indexOf('/cart') === 0 || path.indexOf('/checkout') === 0 || path.indexOf('/pages/sell-your-device') === 0) return;

    // 2026-09-03: homepage only. Product, collection, and repair-form sessions are never interrupted.

    if (path !== '/') return;

    if (localStorage.getItem('mr_popup_dismissed') === 'true') return;

    var closeBtn = overlay.querySelector('.newsletter-popup__close');
    var emailInput = overlay.querySelector('input[type="email"]');
    var pageHeight = document.documentElement.scrollHeight;
    var pageLoadTime = Date.now();
    var shown = false;
    var lastFocused = null;

    function showPopup() {
      if (shown) return;
      shown = true;

      // Listeners are no-ops once shown — detach to free the main thread
      document.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('scroll', onScroll);

      lastFocused = document.activeElement;

      // Once per visitor: mark on show, not only on close, so a back-navigation never re-fires it

      try { localStorage.setItem('mr_popup_dismissed', 'true'); } catch (_) {}
      overlay.classList.add('newsletter-popup-overlay--visible');
      overlay.style.display = '';
      lockBodyScroll();

      // Move focus into the dialog and trap Tab cycling
      if (emailInput) {
        try { emailInput.focus({ preventScroll: true }); } catch (_) { emailInput.focus(); }
      }
      trapFocus(overlay);
    }

    function hidePopup() {
      overlay.classList.remove('newsletter-popup-overlay--visible');
      unlockBodyScroll();
      localStorage.setItem('mr_popup_dismissed', 'true');

      // Restore focus to the element that had it before the popup opened
      if (lastFocused && typeof lastFocused.focus === 'function') {
        try { lastFocused.focus({ preventScroll: true }); } catch (_) { lastFocused.focus(); }
      }
    }

    // Trigger 1: exit-intent (mouse leaves top of viewport)
    function onMouseLeave(e) {
      if (e.clientY <= 0) showPopup();
    }
    document.addEventListener('mouseleave', onMouseLeave);

    // Trigger 2: scroll past 50% of page (fallback for touch devices),
    // but only after a 15-second grace window so fast mobile flicks
    // don't get interrupted before the user has read anything.
    function onScroll() {
      if (Date.now() - pageLoadTime < 15000) return;
      var scrolled = window.scrollY + window.innerHeight;
      if (pageHeight > 0 && scrolled / pageHeight >= 0.5) showPopup();
    }
    window.addEventListener('scroll', onScroll, { passive: true });

    // Refresh cached pageHeight on resize (lazy-loaded images, font swaps)
    window.addEventListener('resize', function () {
      pageHeight = document.documentElement.scrollHeight;
    }, { passive: true });

    // Close button
    if (closeBtn) {
      closeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        hidePopup();
      });
    }

    // Close on overlay click (not inner popup)
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) hidePopup();
    });

    // Close on Escape
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay.classList.contains('newsletter-popup-overlay--visible')) hidePopup();
    });
  }

  /* ==========================================================================
     13. Collection Filters -- DEAD CODE removed in audit cleanup.
     The v2 filter drawer + active-chips logic lives in the Phase 2B IIFE at end of file.
     The legacy selectors (.collection-toolbar__filter-btn, .collection-filters,
     .collection-filters__close) are not in v2 markup.
     ========================================================================== */

  function initCollectionFilters() {
    // No-op: replaced by Phase 2B initDrawer + initActiveChips IIFE.
  }

  /* ==========================================================================
     14. Quantity Selectors
     ========================================================================== */

  function initQuantitySelectors() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.quantity__btn, .product__quantity-btn');
      if (!btn) return;

      var wrapper = btn.closest('.product__quantity-selector') || btn.closest('.quantity');
      if (!wrapper) return;

      // Skip if this is a cart drawer quantity (handled separately)
      if (btn.closest('.cart-drawer')) return;

      var input = wrapper.querySelector('input[type="number"]');
      if (!input) return;

      var currentVal = parseInt(input.value, 10) || 1;
      var min = parseInt(input.getAttribute('min'), 10) || 1;
      var max = parseInt(input.getAttribute('max'), 10) || 99;
      var isMinus = btn.classList.contains('product__quantity-btn--minus') || btn.classList.contains('quantity__btn--minus') || btn.getAttribute('data-action') === 'decrease' || btn.hasAttribute('data-quantity-minus');
      var action = isMinus ? 'decrease' : 'increase';

      if (action === 'increase') {
        if (currentVal < max) {
          input.value = currentVal + 1;
        }
      } else if (action === 'decrease') {
        if (currentVal > min) {
          input.value = currentVal - 1;
        }
      }

      // Dispatch change event for any listeners
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // Prevent manual input below min or above max
    document.addEventListener('change', function (e) {
      if (!e.target.matches('.quantity__input')) return;
      var input = e.target;
      var min = parseInt(input.getAttribute('min'), 10) || 1;
      var max = parseInt(input.getAttribute('max'), 10) || 99;
      var val = parseInt(input.value, 10);

      if (isNaN(val) || val < min) {
        input.value = min;
      } else if (val > max) {
        input.value = max;
      }
    });
  }

  /* ==========================================================================
     14b. Cart Page — AJAX quantity, remove, and clear
     ========================================================================== */

  function initCartPage() {
    var cartForm = document.getElementById('cart-form');
    if (!cartForm) return;

    // AJAX quantity change on +/- click (cart page)
    cartForm.addEventListener('click', function (e) {
      var qtyBtn = e.target.closest('.quantity__btn');
      if (!qtyBtn) return;

      var cartItem = qtyBtn.closest('.cart-line, .cart-item');
      if (!cartItem) return;

      var input = cartItem.querySelector('.quantity__input');
      if (!input) return;

      var line = parseInt(cartItem.getAttribute('data-line'), 10);
      var newQty = parseInt(input.value, 10) || 1;

      // The initQuantitySelectors handler already updated input.value,
      // so read the new value after a tick
      setTimeout(function () {
        newQty = parseInt(input.value, 10);
        if (isNaN(newQty) || newQty < 0) newQty = 0;
        cartPageUpdateLine(line, newQty);
      }, 0);
    });

    // AJAX remove button
    cartForm.addEventListener('click', function (e) {
      var removeBtn = e.target.closest('.cart-line__remove, .cart-item__remove');
      if (!removeBtn) return;
      e.preventDefault();

      var cartItem = removeBtn.closest('.cart-line, .cart-item');
      if (!cartItem) return;

      var line = parseInt(cartItem.getAttribute('data-line'), 10);
      cartItem.style.opacity = '0.4';
      cartPageUpdateLine(line, 0);
    });
  }

  function cartPageUpdateLine(line, quantity) {
    // I2 fix: prevent overlapping AJAX from spam-clicking +/- before reload
    if (window.__cartUpdateInFlight) return;
    window.__cartUpdateInFlight = true;
    document.querySelectorAll('.cart-line .quantity__btn, .cart-line__remove').forEach(function (b) { b.disabled = true; b.style.pointerEvents = 'none'; });
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Cart update failed');
        window.location.reload();
      })
      .catch(function () {
        window.location.reload();
      });
  }

  /* ==========================================================================
     15. Free Shipping Bar Update
     ========================================================================== */

  function updateShippingBar(cartTotalCents) {
    var bar = document.querySelector('.free-shipping-bar');
    if (!bar) return;

    var progressFill = bar.querySelector('.free-shipping-bar__fill');
    var messageEl = bar.querySelector('[data-shipping-text]');
    if (!progressFill) return;

    var drawer = document.querySelector('.cart-drawer');
    var thresholdAttr = drawer ? drawer.getAttribute('data-shipping-threshold') : null;
    var thresholdCents = thresholdAttr ? parseInt(thresholdAttr, 10) * 100 : 10000;

    if (cartTotalCents >= thresholdCents) {
      progressFill.style.width = '100%';
      if (messageEl) messageEl.textContent = 'You qualify for free shipping!';
      bar.classList.add('free-shipping-bar--achieved');
    } else {
      var remaining = thresholdCents - cartTotalCents;
      var percentage = Math.min((cartTotalCents / thresholdCents) * 100, 100);
      progressFill.style.width = percentage + '%';
      if (messageEl) messageEl.textContent = 'You are ' + formatMoney(remaining) + ' away from free shipping!';
      bar.classList.remove('free-shipping-bar--achieved');
    }
  }

  /* ==========================================================================
     16. Scroll-to-Top
     ========================================================================== */

  function initScrollToTop() {
    var btn = document.querySelector('.scroll-to-top');
    if (!btn) return;

    var scrollThreshold = 600;

    window.addEventListener('scroll', function () {
      if (window.scrollY > scrollThreshold) {
        btn.classList.add('is-visible');
      } else {
        btn.classList.remove('is-visible');
      }
    }, { passive: true });

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    });
  }

  /* ==========================================================================
     17. Lazy Loading Enhancement
     ========================================================================== */

  function initLazyLoading() {
    // Only run fallback if native lazy loading is not supported
    if ('loading' in HTMLImageElement.prototype) return;

    var lazyImages = document.querySelectorAll('img[loading="lazy"]');
    if (lazyImages.length === 0) return;

    var imageObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
          }
          if (img.dataset.srcset) {
            img.srcset = img.dataset.srcset;
          }
          img.classList.add('is-loaded');
          imageObserver.unobserve(img);
        }
      });
    }, {
      rootMargin: '200px 0px'
    });

    lazyImages.forEach(function (img) {
      imageObserver.observe(img);
    });
  }

  /* ==========================================================================
     Back-in-Stock Notification Toggle
     ========================================================================== */

  function initBackInStock() {
    var trigger = document.querySelector('[data-bis-trigger]');
    var form = document.querySelector('[data-bis-form]');
    if (!trigger || !form) return;

    trigger.addEventListener('click', function () {
      trigger.style.display = 'none';
      form.style.display = '';
      var input = form.querySelector('input[type="email"]');
      if (input) input.focus();
    });

    /* Update back-in-stock visibility when variant changes */
    document.addEventListener('variant:change', function (e) {
      var bis = document.querySelector('[data-back-in-stock]');
      if (!bis) return;
      var variant = e.detail && e.detail.variant;
      if (variant && variant.available) {
        bis.style.display = 'none';
      } else {
        bis.style.display = '';
        trigger.style.display = '';
        form.style.display = 'none';
      }
      /* Update the hidden note field with new variant info */
      var note = bis.querySelector('[data-bis-note]');
      if (note && variant) {
        note.value = 'Back in stock request: ' + (e.detail.productTitle || '') + ' | ' + (e.detail.productHandle || '') + ' | Variant: ' + (variant.title || '') + ' | ID: ' + variant.id;
      }
    });
  }

  /* ==========================================================================
     N. Brand Slider Pause Toggle (WCAG 2.2.2)
     ========================================================================== */

  function initBrandSliderPause() {
    var btn = document.querySelector('.brand-slider__pause-toggle');
    var track = document.querySelector('.brand-slider__track');
    if (!btn || !track) return;

    var STORAGE_KEY = 'mr_brand_slider_paused';

    function setPaused(paused) {
      if (paused) {
        track.classList.add('brand-slider__track--paused');
        btn.setAttribute('aria-pressed', 'true');
        btn.setAttribute('aria-label', 'Resume brand slider');
        try { localStorage.setItem(STORAGE_KEY, 'true'); } catch (_) {}
      } else {
        track.classList.remove('brand-slider__track--paused');
        btn.setAttribute('aria-pressed', 'false');
        btn.setAttribute('aria-label', 'Pause brand slider');
        try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
      }
    }

    // Restore prior preference
    try {
      if (localStorage.getItem(STORAGE_KEY) === 'true') setPaused(true);
    } catch (_) {}

    btn.addEventListener('click', function () {
      var currentlyPaused = btn.getAttribute('aria-pressed') === 'true';
      setPaused(!currentlyPaused);
    });
  }

  /* ==========================================================================
     Initialize All Modules
     ========================================================================== */

  function safeInit(name, fn) {
    try { fn(); } catch (e) { console.error(name + ' init failed:', e); }
  }

  function initAll() {
    safeInit('MobileMenu', initMobileMenu);
    safeInit('MegaMenu', initMegaMenu);
    safeInit('AnnouncementBar', initAnnouncementBar);
    safeInit('PredictiveSearch', initPredictiveSearch);
    safeInit('AjaxCart', initAjaxCart);
    safeInit('CartDrawer', initCartDrawer);
    safeInit('CartDrawerEvents', initCartDrawerEvents);
    safeInit('VariantSelector', initVariantSelector);
    safeInit('ProductGallery', initProductGallery);
    safeInit('ProductTabs', initProductTabs);
    safeInit('StickyAddBar', initStickyAddBar);
    safeInit('NewsletterPopup', initNewsletterPopup);
    safeInit('CollectionFilters', initCollectionFilters);
    safeInit('QuantitySelectors', initQuantitySelectors);
    safeInit('CartPage', initCartPage);
    safeInit('ScrollToTop', initScrollToTop);
    safeInit('LazyLoading', initLazyLoading);
    safeInit('BackInStock', initBackInStock);
    safeInit('CategoryFilters', initCategoryFilters);
    safeInit('BrandSliderPause', initBrandSliderPause);
    safeInit('ShipTimer', initShipTimer);
    safeInit('SearchShortcut', initSearchShortcut);
    safeInit('SearchPlaceholderRotate', initSearchPlaceholderRotate);
    safeInit('HeaderScrollCollapse', initHeaderScrollCollapse);
    safeInit('MobileHeaderDrawer', initMobileHeaderDrawer);
  }

  /* ==========================================================================
     Category Filters (title-based, auto-generated)
     ========================================================================== */

  function initCategoryFilters() {
    var container = document.getElementById('category-filters');
    if (!container) return;

    var grid = document.querySelector('.collection-grid') || document.querySelector('.search-results');
    if (!grid) { container.style.display = 'none'; return; }
    var cards = Array.from(grid.querySelectorAll('.product-card'));
    if (cards.length === 0) { container.style.display = 'none'; return; }

    var filters = { brand: {}, device: {}, part: {} };
    var activeFilters = { brand: null, device: null, part: null };

    /* Scan cards for data attributes */
    cards.forEach(function (card) {
      var brand = card.getAttribute('data-brand') || '';
      var device = card.getAttribute('data-device') || '';
      var part = card.getAttribute('data-part') || '';
      if (brand) filters.brand[brand] = (filters.brand[brand] || 0) + 1;
      if (device) filters.device[device] = (filters.device[device] || 0) + 1;
      if (part) filters.part[part] = (filters.part[part] || 0) + 1;
    });

    /* Build chips for each filter group */
    ['brand', 'device', 'part'].forEach(function (key) {
      var chipContainer = document.getElementById('filter-chips-' + key);
      var group = container.querySelector('[data-filter="' + key + '"]');
      if (!chipContainer || !group) return;

      var entries = Object.entries(filters[key]).sort(function (a, b) { return b[1] - a[1]; });
      if (entries.length <= 1) { group.style.display = 'none'; return; }

      entries.forEach(function (entry) {
        var value = entry[0];
        var count = entry[1];
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'category-chip';
        chip.setAttribute('data-filter-key', key);
        chip.setAttribute('data-filter-value', value);
        chip.textContent = value + ' (' + count + ')';
        chip.addEventListener('click', function () {
          if (activeFilters[key] === value) {
            activeFilters[key] = null;
            chip.classList.remove('category-chip--active');
          } else {
            var siblings = chipContainer.querySelectorAll('.category-chip');
            siblings.forEach(function (s) { s.classList.remove('category-chip--active'); });
            activeFilters[key] = value;
            chip.classList.add('category-chip--active');
          }
          applyFilters();
        });
        chipContainer.appendChild(chip);
      });
    });

    /* Show/hide "Clear Filters" */
    var clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'category-filters__clear';
    clearBtn.textContent = 'Clear All Filters';
    clearBtn.style.display = 'none';
    clearBtn.addEventListener('click', function () {
      activeFilters.brand = null;
      activeFilters.device = null;
      activeFilters.part = null;
      container.querySelectorAll('.category-chip--active').forEach(function (c) {
        c.classList.remove('category-chip--active');
      });
      applyFilters();
    });
    container.appendChild(clearBtn);

    /* Auto-activate filters from URL params (e.g. &device=Xbox) */
    var urlParams = new URLSearchParams(window.location.search);
    ['brand', 'device', 'part'].forEach(function (key) {
      var paramValue = urlParams.get(key);
      if (paramValue) {
        var safeValue = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(paramValue) : paramValue.replace(/["\\]/g, '\\$&');
        var chip = container.querySelector('[data-filter-key="' + key + '"][data-filter-value="' + safeValue + '"]');
        if (chip) {
          activeFilters[key] = paramValue;
          chip.classList.add('category-chip--active');
        }
      }
    });
    applyFilters();

    /* Sort dropdown — preserves all current URL filter params */
    var sortSelect = document.getElementById('sort-select');
    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        var anyChipActive = activeFilters.brand || activeFilters.device || activeFilters.part;
        var sortKey = sortSelect.value;

        if (anyChipActive) {
          /* Category chip filters active — sort DOM elements client-side */
          var sorted = cards.slice();
          sorted.sort(function (a, b) {
            switch (sortKey) {
              case 'title-ascending':
                return (a.getAttribute('data-title') || '').localeCompare(b.getAttribute('data-title') || '');
              case 'title-descending':
                return (b.getAttribute('data-title') || '').localeCompare(a.getAttribute('data-title') || '');
              case 'price-ascending':
                return (parseInt(a.getAttribute('data-price')) || 0) - (parseInt(b.getAttribute('data-price')) || 0);
              case 'price-descending':
                return (parseInt(b.getAttribute('data-price')) || 0) - (parseInt(a.getAttribute('data-price')) || 0);
              case 'created-descending':
                return (parseInt(b.getAttribute('data-created')) || 0) - (parseInt(a.getAttribute('data-created')) || 0);
              case 'created-ascending':
                return (parseInt(a.getAttribute('data-created')) || 0) - (parseInt(b.getAttribute('data-created')) || 0);
              default:
                return 0;
            }
          });
          sorted.forEach(function (card) { grid.appendChild(card); });
          applyFilters();
        } else {
          /* No chip filters — server-side sort, preserve all URL filter params */
          var params = new URLSearchParams(window.location.search);
          params.set('sort_by', sortKey);
          var baseUrl = sortSelect.getAttribute('data-collection-url') || window.location.pathname;
          var sep = baseUrl.indexOf('?') >= 0 ? '&' : '?';
          window.location.href = baseUrl + sep + params.toString();
        }
      });
    }

    function applyFilters() {
      var anyActive = activeFilters.brand || activeFilters.device || activeFilters.part;
      clearBtn.style.display = anyActive ? '' : 'none';

      var visibleCount = 0;
      cards.forEach(function (card) {
        var show = true;
        if (activeFilters.brand && card.getAttribute('data-brand') !== activeFilters.brand) show = false;
        if (activeFilters.device && card.getAttribute('data-device') !== activeFilters.device) show = false;
        if (activeFilters.part && card.getAttribute('data-part') !== activeFilters.part) show = false;
        card.style.display = show ? '' : 'none';
        if (show) visibleCount++;
      });

      /* Update count display */
      var countEl = document.querySelector('.collection-toolbar__count');
      if (countEl) {
        if (anyActive) {
          countEl.textContent = visibleCount + ' of ' + cards.length + ' products';
        } else {
          countEl.textContent = cards.length + ' products';
        }
      }
    }
  }

  // Handle both normal and late-loading scenarios
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

})();

/* ==========================================================================
   V2 Phase 2B -- Filter drawer + active chips
   ========================================================================== */
(function () {
  function initDrawer() {
    var drawer = document.getElementById('filter-drawer');
    var openBtn = document.querySelector('[data-drawer-open]');
    if (!drawer || !openBtn) return;

    var overlay = document.querySelector('.v2-drawer__overlay');
    var closeBtns = drawer.querySelectorAll('[data-drawer-close]');
    var lastFocused = null;

    function focusableElements() {
      return drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
    }

    function open() {
      lastFocused = document.activeElement;
      drawer.setAttribute('aria-hidden', 'false');
      if (overlay) overlay.setAttribute('data-open', 'true');
      document.body.classList.add('v2-drawer-open');
      var focusables = focusableElements();
      if (focusables.length) focusables[0].focus();
    }

    function close() {
      drawer.setAttribute('aria-hidden', 'true');
      if (overlay) overlay.removeAttribute('data-open');
      document.body.classList.remove('v2-drawer-open');
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    openBtn.addEventListener('click', open);
    closeBtns.forEach(function (b) { b.addEventListener('click', close); });
    if (overlay) overlay.addEventListener('click', close);

    document.addEventListener('keydown', function (e) {
      if (drawer.getAttribute('aria-hidden') !== 'false') return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key === 'Tab') {
        var focusables = Array.from(focusableElements());
        if (focusables.length === 0) return;
        var first = focusables[0];
        var last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  }

  function initActiveChips() {
    var strip = document.querySelector('[data-active-filters]');
    if (!strip) return;

    var url = new URL(window.location.href);
    var params = url.searchParams;
    var chips = [];

    /* Title-based filters: ?brand=X&device=Y&part=Z */
    ['brand', 'device', 'part'].forEach(function (key) {
      var val = params.get(key);
      if (val) chips.push({ key: key, value: val, label: val, paramName: key });
    });

    /* Shopify-native list filters: filter.v.* and filter.p.* */
    params.forEach(function (val, name) {
      if (/^filter\.v\.|^filter\.p\./.test(name)) {
        chips.push({ key: name, value: val, label: val, paramName: name });
      }
    });

    /* Shopify-native price range */
    var pmin = params.get('filter.v.price.gte');
    var pmax = params.get('filter.v.price.lte');
    if (pmin || pmax) {
      /* dedupe — already pushed individually above. Replace with combined chip */
      chips = chips.filter(function (c) { return c.paramName !== 'filter.v.price.gte' && c.paramName !== 'filter.v.price.lte'; });
      var label = '$' + (pmin || '0') + ' — $' + (pmax || '∞');
      chips.push({ key: 'price', value: label, label: label, paramName: ['filter.v.price.gte', 'filter.v.price.lte'] });
    }

    if (chips.length === 0) return;

    var inner = document.createElement('div');
    inner.className = 'v2-active-chips__inner';

    chips.forEach(function (chip) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'v2-chip';
      btn.setAttribute('aria-label', 'Remove filter: ' + chip.label);
      // C1 fix: use textContent (not innerHTML) to neutralize XSS via URL params; build SVG via DOM
      var labelNode = document.createTextNode(chip.label + ' ');
      btn.appendChild(labelNode);
      var ns = 'http://www.w3.org/2000/svg';
      var svg = document.createElementNS(ns, 'svg');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('stroke', 'currentColor');
      svg.setAttribute('stroke-width', '2');
      var l1 = document.createElementNS(ns, 'line');
      l1.setAttribute('x1', '18'); l1.setAttribute('y1', '6'); l1.setAttribute('x2', '6'); l1.setAttribute('y2', '18');
      var l2 = document.createElementNS(ns, 'line');
      l2.setAttribute('x1', '6'); l2.setAttribute('y1', '6'); l2.setAttribute('x2', '18'); l2.setAttribute('y2', '18');
      svg.appendChild(l1); svg.appendChild(l2);
      btn.appendChild(svg);
      btn.addEventListener('click', function () {
        var newUrl = new URL(window.location.href);
        if (Array.isArray(chip.paramName)) {
          chip.paramName.forEach(function (p) { newUrl.searchParams.delete(p); });
        } else {
          newUrl.searchParams.delete(chip.paramName);
        }
        window.location.href = newUrl.toString();
      });
      inner.appendChild(btn);
    });

    var clearAll = document.createElement('button');
    clearAll.type = 'button';
    clearAll.className = 'v2-chip__clear-all';
    clearAll.textContent = 'Clear all';
    clearAll.addEventListener('click', function () {
      var pathname = window.location.pathname;
      // Search pages: preserve q + type params so the search itself isn't lost
      if (pathname.indexOf('/search') === 0) {
        var keep = new URLSearchParams();
        var current = new URLSearchParams(window.location.search);
        if (current.get('q')) keep.set('q', current.get('q'));
        if (current.get('type')) keep.set('type', current.get('type'));
        var qs = keep.toString();
        window.location.href = pathname + (qs ? '?' + qs : '');
      } else {
        window.location.href = pathname;
      }
    });
    inner.appendChild(clearAll);

    strip.appendChild(inner);
  }

  function initFilterCount() {
    var btn = document.querySelector('[data-drawer-open]');
    if (!btn) return;
    var slot = btn.querySelector('.v2-toolbar__filter-count');
    if (!slot) return;

    var params = new URLSearchParams(window.location.search);
    var count = 0;
    ['brand', 'device', 'part'].forEach(function (k) { if (params.get(k)) count++; });
    var seenPriceRange = false;
    params.forEach(function (val, name) {
      if (/^filter\.v\.|^filter\.p\./.test(name)) {
        if (name === 'filter.v.price.gte' || name === 'filter.v.price.lte') {
          if (!seenPriceRange) { count++; seenPriceRange = true; }
        } else {
          count++;
        }
      }
    });

    if (count > 0) {
      slot.textContent = count;
    } else {
      slot.style.display = 'none';
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initDrawer();
      initActiveChips();
      initFilterCount();
    });
  } else {
    initDrawer();
    initActiveChips();
    initFilterCount();
  }
})();

/* ==========================================================================
   V2 Phase 2C -- PDP gallery zoom lens + lightbox
   ========================================================================== */
(function () {
  function initZoomLens() {
    var main = document.querySelector('.product__gallery-main[data-zoom]');
    if (!main) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    var img = main.querySelector('img');
    if (!img) return;

    var lens = document.createElement('div');
    lens.className = 'product__zoom-lens';
    main.appendChild(lens);

    var result = document.createElement('div');
    result.className = 'product__zoom-result';
    main.appendChild(result);

    function setup() {
      var rect = main.getBoundingClientRect();
      var zoom = 2;
      var lensSize = Math.min(rect.width, rect.height) / zoom;
      lens.style.width = lensSize + 'px';
      lens.style.height = lensSize + 'px';

      var bgUrl = img.currentSrc || img.src;
      result.style.backgroundImage = 'url("' + bgUrl + '")';
      result.style.backgroundSize = (rect.width * zoom) + 'px ' + (rect.height * zoom) + 'px';
    }

    function move(e) {
      var rect = main.getBoundingClientRect();
      var x = e.clientX - rect.left;
      var y = e.clientY - rect.top;
      var lensRect = lens.getBoundingClientRect();
      var lensW = lensRect.width;
      var lensH = lensRect.height;

      var lensX = x - lensW / 2;
      var lensY = y - lensH / 2;
      lensX = Math.max(0, Math.min(lensX, rect.width - lensW));
      lensY = Math.max(0, Math.min(lensY, rect.height - lensH));

      lens.style.left = lensX + 'px';
      lens.style.top = lensY + 'px';

      var ratioX = (rect.width - lensW) > 0 ? lensX / (rect.width - lensW) : 0;
      var ratioY = (rect.height - lensH) > 0 ? lensY / (rect.height - lensH) : 0;
      var bgRect = result.getBoundingClientRect();
      var bgW = parseFloat(result.style.backgroundSize.split(' ')[0]);
      var bgH = parseFloat(result.style.backgroundSize.split(' ')[1]);
      result.style.backgroundPosition = '-' + (ratioX * (bgW - bgRect.width)) + 'px -' + (ratioY * (bgH - bgRect.height)) + 'px';
    }

    main.addEventListener('mouseenter', function () {
      setup();
      main.setAttribute('data-zoom-active', 'true');
    });
    main.addEventListener('mouseleave', function () {
      main.removeAttribute('data-zoom-active');
    });
    main.addEventListener('mousemove', move);

    /* Re-setup when image swaps (thumbnail click) */
    var observer = new MutationObserver(function () {
      if (main.getAttribute('data-zoom-active') === 'true') setup();
    });
    observer.observe(img, { attributes: true, attributeFilter: ['src', 'srcset'] });
  }

  function initLightbox() {
    var main = document.querySelector('.product__gallery-main[data-lightbox-trigger]');
    if (!main) return;

    var dialog = document.createElement('dialog');
    dialog.className = 'product__lightbox';
    dialog.setAttribute('aria-label', 'Product image -- full size');
    dialog.innerHTML = '<div class="product__lightbox-inner">' +
      '<button type="button" class="product__lightbox-close" aria-label="Close lightbox">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
      '</button>' +
      '<img class="product__lightbox-img" alt="" />' +
      '</div>';
    document.body.appendChild(dialog);

    var lightboxImg = dialog.querySelector('.product__lightbox-img');
    var closeBtn = dialog.querySelector('.product__lightbox-close');
    var lastFocus = null;

    function open() {
      lastFocus = document.activeElement;
      var img = main.querySelector('img');
      if (!img) return;
      var src = img.currentSrc || img.src;
      /* Prefer 1600w image_url variant if available */
      var hiRes = src.replace(/_\d+x\d+/, '_1600x1600').replace(/width=\d+/, 'width=1600');
      lightboxImg.src = hiRes;
      lightboxImg.alt = img.alt || '';
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', 'open');
      }
      closeBtn.focus();
    }

    function close() {
      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }

    main.addEventListener('click', function (e) {
      e.preventDefault();
      open();
    });
    closeBtn.addEventListener('click', close);

    /* Click backdrop closes */
    dialog.addEventListener('click', function (e) {
      var inner = dialog.querySelector('.product__lightbox-inner');
      if (e.target === dialog || (e.target === inner && e.offsetY > 0)) {
        /* Click outside the image or button */
        var rect = lightboxImg.getBoundingClientRect();
        var btnRect = closeBtn.getBoundingClientRect();
        var inImg = e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
        var inBtn = e.clientX >= btnRect.left && e.clientX <= btnRect.right && e.clientY >= btnRect.top && e.clientY <= btnRect.bottom;
        if (!inImg && !inBtn) close();
      }
    });

    /* Escape key -- handled natively by <dialog>, but add safety */
    dialog.addEventListener('cancel', function (e) {
      e.preventDefault();
      close();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initZoomLens();
      initLightbox();
    });
  } else {
    initZoomLens();
    initLightbox();
  }
})();
